import React, { useState } from 'react';
import { 
  Star, Heart, ShoppingCart, User, Mail, Phone, MapPin, 
  Bell, Settings, Search, Menu, X, ChevronDown, Play,
  Calendar, Clock, Download, Upload, Edit, Trash2,
  MessageCircle, ThumbsUp, Share2, Bookmark, Eye,
  Shield, Award, Zap, Target, TrendingUp, Users, Plus,
  AlertTriangle, CheckCircle, Info, AlertCircle, XCircle
} from 'lucide-react';
import { ComponentShowcaseProps } from '../types/color';

export const ComponentShowcase: React.FC<ComponentShowcaseProps> = ({ palette }) => {
  const [primary, secondary, accent, dark, darkest] = palette.colors;
  const [activeTab, setActiveTab] = useState('light');
  const [showModal, setShowModal] = useState(false);

  const isDarkMode = activeTab === 'dark';
  const bgColor = isDarkMode ? darkest : '#ffffff';
  const textColor = isDarkMode ? '#ffffff' : dark;
  const mutedTextColor = isDarkMode ? '#cbd5e1' : '#6b7280';
  const surfaceColor = isDarkMode ? '#1f2937' : '#ffffff';
  const borderColor = isDarkMode ? '#374151' : '#e5e7eb';

  // Get contrasting text color for navigation
  const getContrastingTextColor = (backgroundColor: string) => {
    const hslMatch = backgroundColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (hslMatch) {
      const lightness = parseInt(hslMatch[3]);
      return lightness > 50 ? '#000000' : '#ffffff';
    }
    return '#ffffff';
  };

  const navTextColor = getContrastingTextColor(primary);

  return (
    <div className="w-full max-w-6xl mx-auto">
      {/* Theme Toggle */}
      <div className="flex justify-center mb-8">
        <div className="bg-gray-100 rounded-lg p-1 flex">
          <button
            onClick={() => setActiveTab('light')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              activeTab === 'light' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Light Theme
          </button>
          <button
            onClick={() => setActiveTab('dark')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              activeTab === 'dark' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Dark Theme
          </button>
        </div>
      </div>

      <div 
        className="rounded-xl shadow-2xl overflow-hidden"
        style={{ backgroundColor: bgColor }}
      >
        <div className="p-8 space-y-12">
          {/* Navigation Bar */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Navigation</h3>
            <nav 
              className="rounded-lg p-4 shadow-lg"
              style={{ backgroundColor: primary }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div 
                    className="font-bold text-xl"
                    style={{ color: navTextColor }}
                  >
                    NocRenk
                  </div>
                  <div className="hidden md:flex space-x-6">
                    <a 
                      href="#" 
                      className="px-3 py-2 rounded-md font-medium transition-all"
                      style={{ 
                        color: navTextColor,
                        backgroundColor: `${navTextColor}20`
                      }}
                    >
                      Home
                    </a>
                    <a 
                      href="#" 
                      className="hover:opacity-80 transition-opacity px-3 py-2 rounded-md"
                      style={{ color: navTextColor }}
                    >
                      Gallery
                    </a>
                    <a 
                      href="#" 
                      className="hover:opacity-80 transition-opacity px-3 py-2 rounded-md"
                      style={{ color: navTextColor }}
                    >
                      Tools
                    </a>
                    <a 
                      href="#" 
                      className="hover:opacity-80 transition-opacity px-3 py-2 rounded-md"
                      style={{ color: navTextColor }}
                    >
                      About
                    </a>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    className="hover:opacity-80 transition-opacity p-2 rounded-md"
                    style={{ color: navTextColor }}
                  >
                    <Search size={20} />
                  </button>
                  <button 
                    className="hover:opacity-80 transition-opacity p-2 rounded-md"
                    style={{ color: navTextColor }}
                  >
                    <Bell size={20} />
                  </button>
                  <button 
                    className="hover:opacity-80 transition-opacity p-2 rounded-md"
                    style={{ color: navTextColor }}
                  >
                    <User size={20} />
                  </button>
                </div>
              </div>
            </nav>
          </section>

          {/* Hero Section */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Hero Section</h3>
            <div 
              className="rounded-lg p-12 text-center"
              style={{ 
                background: `linear-gradient(135deg, ${secondary}, ${accent})`,
                color: 'white'
              }}
            >
              <h1 className="text-4xl font-bold mb-4">Beautiful Color Palettes</h1>
              <p className="text-xl mb-8 opacity-90">Create stunning designs with AI-powered color generation</p>
              <div className="flex justify-center space-x-4">
                <button 
                  className="px-8 py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Get Started
                </button>
                <button 
                  className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
                >
                  Learn More
                </button>
              </div>
            </div>
          </section>

          {/* Enhanced Alerts & Notifications */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Alerts & Notifications</h3>
            <div className="space-y-4">
              {/* Success Alert */}
              <div 
                className="p-4 rounded-lg border-l-4 flex items-start space-x-3"
                style={{ 
                  backgroundColor: `${primary}15`,
                  borderLeftColor: primary
                }}
              >
                <CheckCircle className="text-green-500 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium" style={{ color: primary }}>Palette Generated Successfully!</p>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-sm mt-1" style={{ color: mutedTextColor }}>
                    Your new color palette has been created and is ready to use in your designs.
                  </p>
                  <div className="mt-2 flex space-x-2">
                    <button 
                      className="text-sm px-3 py-1 rounded-md font-medium"
                      style={{ 
                        backgroundColor: primary,
                        color: 'white'
                      }}
                    >
                      View Palette
                    </button>
                    <button 
                      className="text-sm px-3 py-1 rounded-md font-medium border"
                      style={{ 
                        borderColor: primary,
                        color: primary
                      }}
                    >
                      Export
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Warning Alert */}
              <div 
                className="p-4 rounded-lg border-l-4 flex items-start space-x-3"
                style={{ 
                  backgroundColor: `${accent}15`,
                  borderLeftColor: accent
                }}
              >
                <AlertTriangle className="text-yellow-500 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium" style={{ color: accent }}>Accessibility Warning</p>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-sm mt-1" style={{ color: mutedTextColor }}>
                    Some color combinations may not meet WCAG accessibility standards for text contrast.
                  </p>
                  <div className="mt-2">
                    <button 
                      className="text-sm px-3 py-1 rounded-md font-medium"
                      style={{ 
                        backgroundColor: accent,
                        color: 'white'
                      }}
                    >
                      Check Accessibility
                    </button>
                  </div>
                </div>
              </div>

              {/* Info Alert */}
              <div 
                className="p-4 rounded-lg border-l-4 flex items-start space-x-3"
                style={{ 
                  backgroundColor: `${secondary}15`,
                  borderLeftColor: secondary
                }}
              >
                <Info className="text-blue-500 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium" style={{ color: secondary }}>Pro Tip</p>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-sm mt-1" style={{ color: mutedTextColor }}>
                    Try locking your favorite colors and regenerating to explore variations while keeping key elements.
                  </p>
                </div>
              </div>

              {/* Error Alert */}
              <div 
                className="p-4 rounded-lg border-l-4 flex items-start space-x-3"
                style={{ 
                  backgroundColor: '#fee2e2',
                  borderLeftColor: '#ef4444'
                }}
              >
                <XCircle className="text-red-500 mt-0.5" size={20} />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-red-800">Connection Failed</p>
                    <button className="text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-sm mt-1 text-red-700">
                    Unable to connect to AI service. Switching to local color generation.
                  </p>
                  <div className="mt-2">
                    <button className="text-sm px-3 py-1 bg-red-600 text-white rounded-md font-medium hover:bg-red-700">
                      Retry Connection
                    </button>
                  </div>
                </div>
              </div>

              {/* Toast-style Notifications */}
              <div className="space-y-3">
                <h4 className="font-semibold" style={{ color: textColor }}>Toast Notifications</h4>
                <div className="flex flex-wrap gap-3">
                  <div 
                    className="px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 min-w-[280px]"
                    style={{ backgroundColor: surfaceColor, border: `1px solid ${borderColor}` }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: primary }}
                    ></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: textColor }}>Color copied to clipboard</p>
                      <p className="text-xs" style={{ color: mutedTextColor }}>2 seconds ago</p>
                    </div>
                    <CheckCircle className="text-green-500" size={16} />
                  </div>

                  <div 
                    className="px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 min-w-[280px]"
                    style={{ backgroundColor: surfaceColor, border: `1px solid ${borderColor}` }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: accent }}
                    ></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: textColor }}>Palette saved to favorites</p>
                      <p className="text-xs" style={{ color: mutedTextColor }}>5 seconds ago</p>
                    </div>
                    <Heart className="text-red-500" size={16} />
                  </div>

                  <div 
                    className="px-4 py-3 rounded-lg shadow-lg flex items-center space-x-3 min-w-[280px]"
                    style={{ backgroundColor: surfaceColor, border: `1px solid ${borderColor}` }}
                  >
                    <div 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: secondary }}
                    ></div>
                    <div className="flex-1">
                      <p className="text-sm font-medium" style={{ color: textColor }}>Export completed</p>
                      <p className="text-xs" style={{ color: mutedTextColor }}>10 seconds ago</p>
                    </div>
                    <Download className="text-blue-500" size={16} />
                  </div>
                </div>
              </div>

              {/* Status Badges */}
              <div className="space-y-3">
                <h4 className="font-semibold" style={{ color: textColor }}>Status Badges</h4>
                <div className="flex flex-wrap gap-2">
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-semibold text-white flex items-center space-x-1"
                    style={{ backgroundColor: primary }}
                  >
                    <CheckCircle size={14} />
                    <span>Generated</span>
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-semibold text-white flex items-center space-x-1"
                    style={{ backgroundColor: accent }}
                  >
                    <Clock size={14} />
                    <span>Processing</span>
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1"
                    style={{ 
                      backgroundColor: `${primary}20`,
                      color: primary
                    }}
                  >
                    <Star size={14} />
                    <span>Favorited</span>
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1"
                    style={{ 
                      backgroundColor: '#fee2e2',
                      color: '#dc2626'
                    }}
                  >
                    <AlertCircle size={14} />
                    <span>Failed</span>
                  </span>
                  <span 
                    className="px-3 py-1 rounded-full text-sm font-semibold flex items-center space-x-1"
                    style={{ 
                      backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                      color: textColor
                    }}
                  >
                    <Eye size={14} />
                    <span>Draft</span>
                  </span>
                </div>
              </div>

              {/* Progress Indicators */}
              <div className="space-y-3">
                <h4 className="font-semibold" style={{ color: textColor }}>Progress Indicators</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium" style={{ color: textColor }}>Generating Palette</span>
                      <span className="text-sm" style={{ color: mutedTextColor }}>75%</span>
                    </div>
                    <div 
                      className="w-full rounded-full h-2"
                      style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}
                    >
                      <div 
                        className="h-2 rounded-full transition-all duration-300" 
                        style={{ backgroundColor: primary, width: '75%' }}
                      ></div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium" style={{ color: textColor }}>Accessibility Check</span>
                      <span className="text-sm" style={{ color: mutedTextColor }}>100%</span>
                    </div>
                    <div 
                      className="w-full rounded-full h-2"
                      style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}
                    >
                      <div 
                        className="h-2 rounded-full transition-all duration-300" 
                        style={{ backgroundColor: '#10b981', width: '100%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Buttons */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Buttons</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                style={{ backgroundColor: primary }}
              >
                Primary
              </button>
              <button 
                className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90 hover:shadow-lg"
                style={{ backgroundColor: accent }}
              >
                Secondary
              </button>
              <button 
                className="px-6 py-3 rounded-lg font-semibold border-2 transition-all hover:shadow-lg"
                style={{ 
                  borderColor: primary, 
                  color: primary,
                  backgroundColor: 'transparent'
                }}
              >
                Outline
              </button>
              <button 
                className="px-6 py-3 rounded-lg font-semibold transition-all hover:opacity-90"
                style={{ 
                  backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
                  color: textColor
                }}
              >
                Ghost
              </button>
            </div>
          </section>

          {/* Cards Grid */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Cards</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Product Card */}
              <div 
                className="rounded-lg shadow-lg overflow-hidden"
                style={{ backgroundColor: surfaceColor }}
              >
                <div 
                  className="h-32 w-full"
                  style={{ backgroundColor: secondary }}
                ></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-semibold" style={{ color: textColor }}>Premium Design</h4>
                    <Heart className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors" />
                  </div>
                  <p style={{ color: mutedTextColor }} className="mb-4">
                    Beautiful color combinations for modern interfaces
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold" style={{ color: primary }}>$49</span>
                    <button 
                      className="px-4 py-2 rounded-lg text-white font-semibold flex items-center space-x-2 transition-all hover:opacity-90"
                      style={{ backgroundColor: accent }}
                    >
                      <ShoppingCart size={16} />
                      <span>Buy Now</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Profile Card */}
              <div 
                className="rounded-lg shadow-lg p-6"
                style={{ backgroundColor: surfaceColor }}
              >
                <div className="flex items-center mb-4">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: primary }}
                  >
                    <User className="text-white" size={20} />
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold" style={{ color: textColor }}>Sarah Johnson</h4>
                    <p style={{ color: mutedTextColor }}>UI/UX Designer</p>
                  </div>
                </div>
                <p style={{ color: mutedTextColor }} className="mb-4">
                  "These color palettes have transformed my design workflow completely."
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex space-x-1">
                    {[1,2,3,4,5].map((star) => (
                      <Star key={star} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <button style={{ color: mutedTextColor }} className="hover:opacity-70">
                      <MessageCircle size={16} />
                    </button>
                    <button style={{ color: mutedTextColor }} className="hover:opacity-70">
                      <Share2 size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Stats Card */}
              <div 
                className="rounded-lg shadow-lg p-6"
                style={{ backgroundColor: surfaceColor }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold" style={{ color: textColor }}>Analytics</h4>
                  <TrendingUp className="text-green-500" size={20} />
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span style={{ color: mutedTextColor }} className="text-sm">Views</span>
                      <span style={{ color: textColor }} className="text-sm font-medium">12.5K</span>
                    </div>
                    <div 
                      className="w-full rounded-full h-2"
                      style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}
                    >
                      <div 
                        className="h-2 rounded-full" 
                        style={{ backgroundColor: primary, width: '75%' }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span style={{ color: mutedTextColor }} className="text-sm">Engagement</span>
                      <span style={{ color: textColor }} className="text-sm font-medium">8.2K</span>
                    </div>
                    <div 
                      className="w-full rounded-full h-2"
                      style={{ backgroundColor: isDarkMode ? '#374151' : '#e5e7eb' }}
                    >
                      <div 
                        className="h-2 rounded-full" 
                        style={{ backgroundColor: accent, width: '60%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Forms */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Forms</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div 
                className="rounded-lg shadow-lg p-6"
                style={{ backgroundColor: surfaceColor }}
              >
                <h4 className="text-xl font-semibold mb-4" style={{ color: textColor }}>Contact Form</h4>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: textColor }}>Name</label>
                    <input 
                      type="text" 
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all"
                      style={{ 
                        borderColor: borderColor,
                        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                        color: textColor,
                        '--tw-ring-color': primary
                      } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: textColor }}>Email</label>
                    <input 
                      type="email" 
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all"
                      style={{ 
                        borderColor: borderColor,
                        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                        color: textColor,
                        '--tw-ring-color': primary
                      } as React.CSSProperties}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: textColor }}>Message</label>
                    <textarea 
                      rows={4}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-all"
                      style={{ 
                        borderColor: borderColor,
                        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                        color: textColor,
                        '--tw-ring-color': primary
                      } as React.CSSProperties}
                    ></textarea>
                  </div>
                  <button 
                    type="submit"
                    className="w-full py-3 rounded-lg text-white font-semibold transition-all hover:opacity-90"
                    style={{ backgroundColor: primary }}
                  >
                    Send Message
                  </button>
                </form>
              </div>

              <div 
                className="rounded-lg shadow-lg p-6"
                style={{ backgroundColor: surfaceColor }}
              >
                <h4 className="text-xl font-semibold mb-4" style={{ color: textColor }}>Settings Panel</h4>
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium" style={{ color: textColor }}>Notifications</h5>
                      <p className="text-sm" style={{ color: mutedTextColor }}>Receive email updates</p>
                    </div>
                    <div 
                      className="w-12 h-6 rounded-full p-1 cursor-pointer transition-colors"
                      style={{ backgroundColor: primary }}
                    >
                      <div className="w-4 h-4 bg-white rounded-full transform translate-x-6 transition-transform"></div>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: textColor }}>Theme</label>
                    <select 
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2"
                      style={{ 
                        borderColor: borderColor,
                        backgroundColor: isDarkMode ? '#374151' : '#ffffff',
                        color: textColor
                      }}
                    >
                      <option>Light</option>
                      <option>Dark</option>
                      <option>Auto</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: textColor }}>Language</label>
                    <div className="flex space-x-2">
                      <button 
                        className="px-3 py-1 rounded-md text-sm font-medium text-white"
                        style={{ backgroundColor: primary }}
                      >
                        EN
                      </button>
                      <button 
                        className="px-3 py-1 rounded-md text-sm font-medium border"
                        style={{ 
                          borderColor: primary,
                          color: primary,
                          backgroundColor: 'transparent'
                        }}
                      >
                        ES
                      </button>
                      <button 
                        className="px-3 py-1 rounded-md text-sm font-medium border"
                        style={{ 
                          borderColor: primary,
                          color: primary,
                          backgroundColor: 'transparent'
                        }}
                      >
                        FR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Data Tables */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Data Table</h3>
            <div 
              className="rounded-lg shadow-lg overflow-hidden"
              style={{ backgroundColor: surfaceColor }}
            >
              <div className="p-4 border-b" style={{ borderColor: borderColor }}>
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold" style={{ color: textColor }}>Recent Projects</h4>
                  <div className="flex space-x-2">
                    <button 
                      className="px-3 py-1 rounded-md text-sm font-medium text-white"
                      style={{ backgroundColor: primary }}
                    >
                      <Download size={14} className="inline mr-1" />
                      Export
                    </button>
                    <button 
                      className="px-3 py-1 rounded-md text-sm font-medium border"
                      style={{ 
                        borderColor: primary,
                        color: primary,
                        backgroundColor: 'transparent'
                      }}
                    >
                      <Plus size={14} className="inline mr-1" />
                      Add New
                    </button>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ backgroundColor: isDarkMode ? '#374151' : '#f9fafb' }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: textColor }}>Project</th>
                      <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: textColor }}>Status</th>
                      <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: textColor }}>Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: textColor }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Website Redesign', status: 'In Progress', date: '2025-01-15' },
                      { name: 'Mobile App', status: 'Completed', date: '2025-01-10' },
                      { name: 'Brand Identity', status: 'Review', date: '2025-01-12' }
                    ].map((project, index) => (
                      <tr key={index} className="border-b" style={{ borderColor: borderColor }}>
                        <td className="px-4 py-3" style={{ color: textColor }}>{project.name}</td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium text-white"
                            style={{ 
                              backgroundColor: project.status === 'Completed' ? '#10b981' : 
                                             project.status === 'In Progress' ? primary : accent
                            }}
                          >
                            {project.status}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ color: mutedTextColor }}>{project.date}</td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-2">
                            <button style={{ color: primary }} className="hover:opacity-70">
                              <Edit size={16} />
                            </button>
                            <button style={{ color: mutedTextColor }} className="hover:text-red-500">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Modal Trigger */}
          <section>
            <h3 className="text-2xl font-bold mb-6" style={{ color: textColor }}>Modal & Overlays</h3>
            <button
              onClick={() => setShowModal(true)}
              className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: primary }}
            >
              Open Modal
            </button>
          </section>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div 
            className="rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl"
            style={{ backgroundColor: surfaceColor }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: textColor }}>Confirm Action</h3>
              <button
                onClick={() => setShowModal(false)}
                style={{ color: mutedTextColor }}
                className="hover:opacity-70"
              >
                <X size={20} />
              </button>
            </div>
            <p style={{ color: mutedTextColor }} className="mb-6">
              Are you sure you want to proceed with this action? This cannot be undone.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 rounded-lg font-semibold text-white transition-all hover:opacity-90"
                style={{ backgroundColor: primary }}
              >
                Confirm
              </button>
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 rounded-lg font-semibold border transition-all hover:opacity-70"
                style={{ 
                  borderColor: borderColor,
                  color: textColor,
                  backgroundColor: 'transparent'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};